import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import useSEO from "../Hooks/UseSeo";
import { Helmet } from "react-helmet";

const EscortedTours = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: "Escorted Tours | Premium & Affordable Guided Holidays Worldwide",
    description:
      "Explore our Escorted Tours — guided group travel experiences offering comfort, expert guidance, and perfectly planned itineraries across the globe.",
    url: "https://www.yourtravelwebsite.com/escorted-tours",
  });

  return (
    <>
      <Helmet>
        <link
          rel="canonical"
          href="https://www.yourtravelwebsite.com/escorted-tours"
        />
      </Helmet>

      <Header />

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-12 mt-32">
        {/* ✅ Hero Section */}
        <section className="relative bg-gray-100 rounded-lg overflow-hidden">
          <img
            src="https://img.freepik.com/free-photo/beautiful-girl-standing-boat-looking-mountains-ratchaprapha-dam-khao-sok-national-park-surat-thani-province-thailand_335224-849.jpg?semt=ais_hybrid&w=740&q=80"
            alt="Escorted Tours Hero"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center">
              Escorted Tours Around the World
            </h1>
          </div>
        </section>

        {/* ✅ Introduction */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">What are Escorted Tours?</h2>
          <p>
            One of the most popular and convenient ways to travel internationally is through <b>Escorted Tours</b>.
            These tours are designed for travelers who prefer a stress-free journey without the hassle of planning
            flights, accommodations, and sightseeing arrangements. With the help of an expert guide, you can discover
            destinations in greater depth, enjoy insider knowledge, and explore with complete peace of mind.
          </p>
          <p>
            In simple terms, escorted tours are <b>guided group journeys</b> that follow a pre-planned itinerary. They
            ensure that every part of your vacation — from transport to meals — is handled professionally, letting you
            focus solely on enjoying your travel experience.
          </p>
        </section>

        {/* ✅ Key Advantages */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Key Advantages of Escorted Tours</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <b>Well-Planned Itinerary:</b> Everything from your flight bookings to local activities is carefully
              arranged. You can relax while professionals handle the logistics.
            </li>
            <li>
              <b>Perfect for Large Destinations:</b> Expert planners ensure you visit the best attractions efficiently,
              especially useful in countries where language and culture vary widely.
            </li>
            <li>
              <b>Expert Tour Guides:</b> Gain deeper insights into culture, history, and geography through professional
              guides who make every journey informative and engaging.
            </li>
            <li>
              <b>Hassle-Free Experience:</b> From airport transfers to hotel check-ins, every aspect is managed by the
              tour operator, allowing you to travel worry-free.
            </li>
          </ul>
        </section>

        {/* ✅ Tour Categories */}
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold text-center">
            Discover Our Best Escorted Tours
          </h2>
          <p className="text-center max-w-3xl mx-auto">
            Our escorted tours are carefully crafted to offer immersive experiences across continents — from Europe’s
            historical landmarks to Asia’s vibrant culture. Choose between <b>Premium Holidays</b> for luxurious
            experiences and <b>Affordable Touring</b> for incredible value.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border rounded-lg shadow hover:shadow-lg transition p-4 flex flex-col items-center text-center">
              <img
                src="https://media.istockphoto.com/id/1297349747/photo/hot-air-balloons-flying-over-the-botan-canyon-in-turkey.jpg?s=612x612&w=0&k=20&c=kt8-RRzCDunpxgKFMBBjZ6jSteetNhhSxHZFvHQ0hNU="
                alt="Premium Holidays"
                className="w-full h-56 object-cover rounded"
              />
              <h3 className="text-xl font-semibold mt-3">Premium Holidays</h3>
              <p className="text-sm mt-2">
                Go beyond the ordinary vacation — explore Europe, USA, Canada, Africa, Japan, and more in absolute
                comfort with expert guides and exclusive experiences.
              </p>
              <button
                className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/premium-holidays")}
              >
                Click for Itinerary
              </button>
            </div>

            <div className="border rounded-lg shadow hover:shadow-lg transition p-4 flex flex-col items-center text-center">
              <img
                src="https://images.unsplash.com/photo-1756142754765-663403d56be0?ixlib=rb-4.1.0&ixid=M3wxMjA3fDF8MHxzZWFyY2h8MXx8dHJhdmVsfGVufDB8fDB8fHww&fm=jpg&q=60&w=3000"
                alt="Affordable Touring"
                className="w-full h-56 object-cover rounded"
              />
              <h3 className="text-xl font-semibold mt-3">Affordable Touring</h3>
              <p className="text-sm mt-2">
                Turn your travel dreams into reality with escorted tours across Asia, Europe, Australia, and the
                Americas — affordable, memorable, and expertly managed.
              </p>
              <button
                className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/affordable-touring")}
              >
                Click for Itinerary
              </button>
            </div>
          </div>
        </section>

        {/* ✅ FAQ Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Frequently Asked Questions</h2>
          <div className="space-y-3">
            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What is an escorted group tour?
              </summary>
              <p className="mt-2">
                An escorted tour is a professionally guided group travel package where an expert accompanies travelers
                throughout the itinerary, ensuring smooth coordination and providing insightful information.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What are the benefits of a guided tour?
              </summary>
              <p className="mt-2">
                Guided tours provide convenience, cultural insights, and safety. You can enjoy your trip without
                worrying about logistics or planning every detail.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                How do escorted tours differ from guided tours?
              </summary>
              <p className="mt-2">
                While both offer expert guidance, escorted tours include additional logistical support like
                transportation, meals, and accommodations, ensuring a completely stress-free experience.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                Which is the best international escorted tour?
              </summary>
              <p className="mt-2">
                The best tour depends on your preferences — explore Europe’s culture, Asia’s diversity, or Africa’s
                adventure with our curated itineraries.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What are the most affordable European countries to visit?
              </summary>
              <p className="mt-2">
                Eastern European countries such as Poland, Hungary, and Romania offer rich culture and affordability,
                making them ideal for budget-conscious travelers.
              </p>
            </details>
          </div>
        </section>

        {/* ✅ CTA Section */}
        <section className="text-center py-6">
          <p className="text-lg">
            Ready to experience the world with comfort and confidence? Contact our travel experts today at
            <a
              href="tel:+918447433852"
              className="text-yellow-600 font-semibold ml-1"
            >
              +91-8447433852
            </a>
          </p>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default EscortedTours;
